import React, { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Paperclip, Trash2 } from 'lucide-react';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from '../../utils/firebase';
import type { ScenarioOutflowAttachment } from '../../utils/cashFlowScenario';
import {
  CAPEX_LINE_ATTACHMENT_ACCEPT,
  capexLineAttachmentPreviewKind,
} from '../../utils/capitalPlans';
import {
  deleteCapitalPlanLineAttachment,
  uploadCapitalPlanLineAttachment,
} from '../../utils/capitalPlanService';

function CapexLineFileLink({
  file,
  onOpen,
}: {
  file: ScenarioOutflowAttachment;
  onOpen?: (href: string, file: ScenarioOutflowAttachment) => void;
}) {
  const [href, setHref] = useState(file.url || '');
  const kind = capexLineAttachmentPreviewKind(file);
  const Icon = kind === 'image' ? ImageIcon : FileText;

  useEffect(() => {
    if (file.url) {
      setHref(file.url);
      return;
    }
    if (!file.storagePath) return;
    let cancelled = false;
    getDownloadURL(storageRef(storage, file.storagePath))
      .then((url) => {
        if (!cancelled) setHref(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [file.url, file.storagePath]);

  const body = (
    <>
      <Icon size={14} aria-hidden />
      <span>{file.name}</span>
    </>
  );

  if (href && onOpen) {
    return (
      <button type="button" className="fin-capex-file-link" onClick={() => onOpen(href, file)}>
        {body}
      </button>
    );
  }
  if (href) {
    return (
      <a className="fin-capex-file-link" href={href} target="_blank" rel="noreferrer">
        {body}
      </a>
    );
  }
  return <span className="fin-capex-file-link">{body}</span>;
}

const CapexLineAttachments: React.FC<{
  lineId: string;
  attachments: ScenarioOutflowAttachment[] | undefined;
  canWrite: boolean;
  planId: string | null;
  onEnsurePlanId?: () => Promise<string | null>;
  onChange: (next: ScenarioOutflowAttachment[]) => void;
  onPreview: (href: string, file: ScenarioOutflowAttachment) => void;
}> = ({
  lineId,
  attachments,
  canWrite,
  planId,
  onEnsurePlanId,
  onChange,
  onPreview,
}) => {
  const files = attachments || [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = async (picked: File[]) => {
    if (!picked.length) return;
    setBusy(true);
    setError(null);
    try {
      const id = planId || (await onEnsurePlanId?.()) || null;
      if (!id) throw new Error('Save this plan before attaching files.');
      const uploaded: ScenarioOutflowAttachment[] = [];
      for (const file of picked) {
        uploaded.push(await uploadCapitalPlanLineAttachment({ planId: id, lineId, file }));
      }
      onChange([...files, ...uploaded]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to attach file.');
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (file: ScenarioOutflowAttachment) => {
    setBusy(true);
    setError(null);
    try {
      await deleteCapitalPlanLineAttachment(file.storagePath);
      onChange(files.filter((row) => row.id !== file.id && row.storagePath !== file.storagePath));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to remove file.');
    } finally {
      setBusy(false);
    }
  };

  if (!canWrite && files.length === 0) return null;

  return (
    <div className="fin-capex-files">
      <span className="fin-modal-label">PDFs &amp; photos</span>
      {files.length > 0 && (
        <ul className="fin-capex-file-list">
          {files.map((file) => (
            <li key={file.id || file.storagePath} className="fin-capex-file-row">
              <CapexLineFileLink file={file} onOpen={onPreview} />
              {canWrite && (
                <button
                  type="button"
                  className="fin-icon-quiet"
                  aria-label={`Remove ${file.name}`}
                  disabled={busy}
                  onClick={() => void removeFile(file)}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
        <label className={`fin-capex-file-add${files.length === 0 ? ' is-primary' : ''}`}>
          <input
            type="file"
            accept={CAPEX_LINE_ATTACHMENT_ACCEPT}
            multiple
            disabled={busy}
            className="fin-capex-file-hidden"
            onChange={(e) => {
              const next = Array.from(e.target.files || []);
              e.target.value = '';
              if (!next.length) return;
              void addFiles(next);
            }}
          />
          <Paperclip size={13} aria-hidden />
          {busy ? 'Uploading…' : files.length ? 'Add another' : 'Add PDF or image'}
        </label>
      )}
      {error && <p className="fin-capex-file-error">{error}</p>}
    </div>
  );
};

export default CapexLineAttachments;
